/**
 * Tree builder for the rules-engine QueryBuilder field picker.
 *
 * The BE emits `group: string[]` on every condition field via
 * `walk_claim_model` → `FieldDefinition.group`; we rebuild a `SuggestionOption`
 * tree from the flat `Field[]` list RQB hands us so the shared
 * `HierarchicalSearchablePopover` can render it the same way it renders the
 * template-token tree.
 */

import type { Field } from 'react-querybuilder';

import type { SuggestionOption } from '~/components/core/Editor/types';

export type FieldOption = Field & { group?: string[] };

// `\0` (NUL) is used as the path-key separator instead of a printable
// character — group names come from BE-supplied claim-model labels which can
// contain `>`, `/`, `:` etc.; NUL is guaranteed not to appear in user-facing
// strings.
const PATH_SEPARATOR = '\0';

/**
 * Build a `SuggestionOption[]` tree from a flat list of fields. Each field's
 * `group: string[]` is the BE-supplied hierarchy (e.g. `['Claim', 'Incident Details']`).
 * Fields without a group land at the root.
 */
export function buildFieldTree(options: ReadonlyArray<FieldOption>): SuggestionOption[] {
  const root: SuggestionOption[] = [];
  const groupByPath = new Map<string, SuggestionOption>();
  for (const opt of options) {
    const groupChain = opt.group ?? [];
    let level = root;
    let path = '';
    for (const groupName of groupChain) {
      path = path ? `${path}${PATH_SEPARATOR}${groupName}` : groupName;
      let group = groupByPath.get(path);
      if (!group) {
        group = { id: `group_${path}`, label: groupName, sub_items: [] };
        groupByPath.set(path, group);
        level.push(group);
      }
      level = group.sub_items as SuggestionOption[];
    }
    level.push({ id: opt.name, label: opt.label });
  }
  return root;
}
